import { memo, useState } from 'react';

import dayjs from 'dayjs';

import Skeleton from '../Skeleton/Skeleton';

import Avatar from '@/components/common/Avatar/Avatar';
import { GetHistoryAudit } from '@/graphql/contracts/_generated_/getHistoryAudit.query';
import { validateDateFormat } from '@/utils/date.utils';
import {
  DATE_TIME_PATTERN,
  DEFAULT_DATE_PATTERN,
  ISO_DATE_FORMAT,
  ISO_DATE_FORMAT_WITH_NANOSECONDS
} from '@/config/common.config';

import CollapsibleIcon from './components/CollapsibleIcon';
import styles from './history-card.module.scss';
import { EventSourceTypeEnum, EventTypeEnum } from '@/graphql/contracts/_generated_/contracts';
import { User } from '@/hooks/identity/responseModels/user';

type StaticDataItem = GetHistoryAudit['history'][number]['staticData'][number];
type ChangesItem = GetHistoryAudit['history'][number]['changes'][number];

interface IHistoryCardProps {
  historyItem: GetHistoryAudit['history'][number];
  user: User | null | undefined
  isLoading: boolean
}

function HistoryCard({ historyItem, user, isLoading }: IHistoryCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isSystemUser = historyItem.eventSourceType === EventSourceTypeEnum.SYSTEM;

  const transformIfDateValueIncluded = (value: string) => {
    const dateFormatReceived = 'YYYY-MM-DDTHH:mm:ss';
    const dateInSentence = value.match(ISO_DATE_FORMAT) ? value.match(ISO_DATE_FORMAT)?.join() : null;
    // for single date case
    if (validateDateFormat(value, dateFormatReceived)) {
      return dayjs(value).format(DEFAULT_DATE_PATTERN);
    }
    // for rare cases when date could be within the sentence

    if (dateInSentence) {
      return value.match(ISO_DATE_FORMAT_WITH_NANOSECONDS)
        ? dayjs(dateInSentence).format(DEFAULT_DATE_PATTERN)
        : value.replace(dateInSentence, dayjs(dateInSentence).format(DEFAULT_DATE_PATTERN));
    }

    return value;
  };

  const replaceUserNamePlaceholder = (el: StaticDataItem) => {
    if (el.name === 'User Name') {
      if (isSystemUser) {
        return 'System';
      }

      return `${user?.FirstName ?? ''} ${user?.LastName ?? ''}`.trim() || 'unavailable';
    }

    return transformIfDateValueIncluded(el.value ?? '');
  };

  const getUserDisplayName = () => {
    if (isSystemUser) {
      return 'System';
    }
    return `${user?.FirstName ?? ''} ${user?.LastName ?? ''}`.trim() || 'unavailable';
  };

  const filteredStaticData = 
    historyItem.staticData?.filter(el => el.name !== 'User Name' && el.name !== 'System') || [];

  return (
    <div className={styles['audit-details']}>
      <div className={styles.wrapper} onClick={() => setIsExpanded(!isExpanded)}>
        <div className={styles['audit-info']}>
          <Avatar
            user={user}
            size={47}
            isLoading={isLoading}
          />

          <div className={styles['info-wrapper']}>
            <div className={styles['info-title']}>
              {historyItem.header}
            </div>
            <div className={styles['info-user']}>
              {getUserDisplayName()}
            </div>
            <div className={styles['info-date']}>
              {dayjs(historyItem.timeStamp).format(DATE_TIME_PATTERN)}
            </div>
          </div>
        </div>

        <CollapsibleIcon expanded={isExpanded} onClick={() => setIsExpanded(!isExpanded)} />
      </div>

      {filteredStaticData && filteredStaticData.length > 0 && (
        <div className={styles['base-details']}>
          {filteredStaticData.map((el: StaticDataItem, i: number) => (
            <div key={i} className={styles.detail}>
              <span>{el.name}</span>
              <span className={styles['large-word-break']}>
                {isLoading ? (
                  <Skeleton
                    width={150}
                    height={15}
                  />
                ) : replaceUserNamePlaceholder(el)}
              </span>
            </div>
          ))}
        </div>
      )}

      {isExpanded && historyItem.changes && historyItem.changes.length > 0 && (
        <div className={styles['change-details']}>
          {historyItem.changes.map((el: ChangesItem) => (
            <div key={el.name} className={styles['change-item']}>
              <div className={styles['field-name']}>{el.name}</div>
              <div className={styles['change-values']}>
                {historyItem.eventType !== EventTypeEnum.CREATED && el.oldValue.trim() !== '' && (
                  <span className={styles['prior-value']}>
                    {transformIfDateValueIncluded(el.oldValue)}
                  </span>
                )}
                {historyItem.eventType !== EventTypeEnum.CREATED &&
                 el.oldValue.trim() === '' && el.newValue.trim() !== '' && (
                  <span className={styles['prior-value']}>
                    <span className={styles['empty-placeholder']}>&nbsp;</span>
                  </span>
                )}
                {historyItem.eventType !== EventTypeEnum.CREATED &&
                 (el.oldValue.trim() !== '' ||
                  (el.oldValue.trim() === '' && el.newValue.trim() !== '')) && (
                  <span className={styles.arrow}>→</span>
                )}
                <span className={styles['new-value']}>
                  {el.newValue.trim() === '' 
                    ? <span className={styles['empty-placeholder']}>&nbsp;</span> 
                    : transformIfDateValueIncluded(el.newValue)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default memo<IHistoryCardProps>(HistoryCard, (prevProps, nextProps) => {
  return prevProps.historyItem.changeSetID === nextProps.historyItem.changeSetID;
});
